import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import { exportReferans } from './api/referansApi'
import { Dropdown } from 'primereact/dropdown'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import RefreshButton from '../../components/ui/RefreshButton'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import { searchTerm } from '../../lib/searchTerm'
import { useToast } from '../../components/ui/ToastProvider'
import { isOptimisticLock } from '../../api/apiError'
import { referansColumns } from './components/ReferansColumns'
import ReferansFormDialog from './components/ReferansFormDialog'
import { useReferans } from './hooks/useReferans'
import type { ReferansItem } from './types'
import Button from '../../components/ui/Button'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
import './admin.css'

/** "Tümü" için boş-OLMAYAN sentinel: PrimeReact Dropdown null/'' değerini "boş seçim" sayıp seçmeyi
 * (onChange'i) yutuyor; bu değer boş olmadığından seçilebilir/tetiklenebilir ve API'ye giderken
 * filtresiz'e (undefined) çevrilir. */
const ALL_TYPES = 'ALL'

/** Başlangıç değeri: hiçbir tip seçilmemiş → Dropdown placeholder'ı ("Tip seçin") görünür, sorgu yapılmaz. */
const NO_TYPE = ''

/**
 * Admin → Referans (015 liste + 018 ad düzenleme). TİP filtresi + ad araması; yalnız aktif kayıtlar.
 *
 * <p><b>Ekran ilk açılışında sorgu yapılmaz</b> (kullanıcı kuralı 2026-07-31): kayıtlar ancak tip
 * combosundan bir değer seçilince <i>veya</i> ad ile arama yapılınca gelir. Tabloda o ana kadar
 * yönlendirici boş durum ("Tip seçin ya da ad ile arayın") görünür. Tip listesi (combo seçenekleri) yine
 * de yüklenir — yoksa kullanıcı seçim yapamazdı.
 *
 * <p>Kısıtlı Yetkili asistan (126) ekranı <b>salt okunur</b> görür: Düzenle eylemi tabloda da kartta
 * da çizilmez; tip ve ad listede zaten göründüğü için görüntüleme pop-up'ına gerek yoktur.
 */
export default function ReferansAdminPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const toast = useToast()
  const { readOnly } = useProxyMode()

  const [type, setType] = useState<string>(NO_TYPE)
  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setQ(searchInput), 400)
    return () => clearTimeout(id)
  }, [searchInput])

  const needle = searchTerm(q)
  // Seçim/arama yoksa liste hiç sorgulanmaz. "Tüm Referans Tipleri" (ALL_TYPES) bilinçli bir seçimdir →
  // sorgu yapılır (filtresiz), ilk açılıştaki "seçim yok" durumundan (NO_TYPE) farklıdır.
  const hasCriteria = type !== NO_TYPE || needle !== ''
  const { list, types, update } = useReferans(type === ALL_TYPES ? '' : type, needle, hasCriteria)
  const rows = hasCriteria ? (list.data?.content ?? []) : []
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const [editing, setEditing] = useState<ReferansItem | null>(null)

  const typeOptions = [
    { label: t('admin.referans.filter.typeAll'), value: ALL_TYPES },
    ...(types.data ?? []).map((ty) => ({ label: ty, value: ty })),
  ]

  const onSubmit = (nameTr: string, nameEn: string | null) => {
    if (!editing) return
    update.mutate(
      { id: editing.id, body: { nameTr, nameEn, modDate: editing.modDate } },
      {
        onSuccess: () => {
          toast.show({
            severity: 'success',
            summary: t('admin.referans.toast.updated'),
            life: 3000,
          })
          setEditing(null)
        },
        onError: (err) => {
          // optimistic-lock/generic toast'ı merkez (019) basar; ekran yalnız çakışmada tazeler.
          if (isOptimisticLock(err)) {
            setEditing(null)
            list.refetch()
          }
        },
      },
    )
  }

  return (
    <div className="admin-page">
      <ReferansFormDialog
        visible={editing != null}
        initial={editing}
        submitting={update.isPending}
        onSubmit={onSubmit}
        onHide={() => setEditing(null)}
      />

      <PageHeader
        title={t('nav.admin.referans')}
        actions={
          <>
            {/* Yenile, Excel'in SOLUNDA (kullanici istegi 2026-08-26). Onceden filtre
                cubugundaydi ve yalniz olcut varken cizilirdi; baslikta HER ZAMAN durur —
                listeyi tazelemek bir filtre eylemi degil. */}
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton
              onExport={() => exportReferans(type === ALL_TYPES ? undefined : type, needle)}
              disabled={rows.length === 0}
            />
          </>
        }
      />

      {/* Tip combosu sağda, arama solda — artık FilterBar VARSAYILANI (2026-08-26); ekran ayrıca
          prop geçmez. "Yenile" bu ekranda filtre çubuğundan sayfa başlığına taşındı. */}
      <FilterBar
        searchValue={searchInput}
        searchPlaceholder={t('admin.referans.search')}
        onSearch={setSearchInput}
      >
        <Dropdown
          value={type}
          options={typeOptions}
          onChange={(e) => setType((e.value as string) ?? NO_TYPE)}
          placeholder={t('admin.referans.filter.typePlaceholder')}
          aria-label={t('admin.referans.filter.typePlaceholder')}
        />
      </FilterBar>

      {isMobile ? (
        <div className="ui-card-list">
          {hasCriteria && list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {(!hasCriteria || (!list.isLoading && rows.length === 0)) && (
            <EmptyState
              title={hasCriteria ? t('admin.referans.empty.title') : t('admin.referans.empty.promptTitle')}
              description={
                hasCriteria ? t('admin.referans.empty.desc') : t('admin.referans.empty.promptDesc')
              }
            />
          )}
          {hasCriteria &&
            !list.isLoading &&
            rows.map((row) => (
              <div key={row.id} className="ui-card">
                <div className="ui-card-top">
                  <span />
                  {!readOnly && (
                    <div className="ui-card-actions ui-card-actions--compact">
                      <Button
                        icon="pi pi-pencil"
                        rounded
                        text
                        aria-label={t('admin.referans.actions.edit')}
                        onClick={() => setEditing(row)}
                      />
                    </div>
                  )}
                </div>
                <div className="ui-card-head">
                  <span className="ui-card-title">{localized(row.nameTr, row.nameEn)}</span>
                </div>
                <span className="ui-card-sub">{row.type}</span>
              </div>
            ))}
        </div>
      ) : (
      <DataTable
        value={rows}
        loading={hasCriteria && list.isLoading}
        sortField="type"
        sortOrder={1}
        emptyMessage={
          hasCriteria ? (
            <EmptyState
              title={t('admin.referans.empty.title')}
              description={t('admin.referans.empty.desc')}
            />
          ) : (
            <EmptyState
              title={t('admin.referans.empty.promptTitle')}
              description={t('admin.referans.empty.promptDesc')}
            />
          )
        }
      >
        {referansColumns({ t, localized, onEdit: setEditing, readOnly })}
      </DataTable>
      )}
    </div>
  )
}
